import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { api } from '../lib/api';
import { signInWithGoogle, signOutEverywhere } from '../lib/auth';
import { isPermission, type Permission } from '../lib/permissions';
import {
  AuthContext,
  initialAuthState,
  type AuthContextValue,
  type AuthState,
} from './auth-context';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialAuthState);

  const refresh = useCallback(async () => {
    try {
      await getCurrentUser();
    } catch {
      setState({ ...initialAuthState, status: 'signed-out' });
      return;
    }

    let name: string | null = null;
    let picture: string | null = null;
    try {
      const attrs = await fetchUserAttributes();
      name = attrs.name ?? attrs.given_name ?? null;
      picture = attrs.picture ?? null;
    } catch {
      // attributes are nice-to-have; ignore failures
    }

    try {
      const me = await api.me();
      const permissions = new Set<Permission>();
      for (const p of me.permissions ?? []) {
        if (isPermission(p)) permissions.add(p);
      }
      setState({
        status: 'signed-in',
        email: me.email,
        role: me.role,
        permissions,
        name,
        picture,
        errorMessage: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setState({
        ...initialAuthState,
        status: 'unauthorized',
        errorMessage: message,
      });
    }
  }, []);

  useEffect(() => {
    // The bootstrap read has to happen here: nothing can be rendered until we
    // know whether there is a session, and the Hub subscription below only
    // reports *changes*. `refresh` reaches its first await before it can set
    // anything, so this is not the cascading-render case the rule targets.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
    const unsub = Hub.listen('auth', ({ payload }) => {
      if (
        payload.event === 'signedIn' ||
        payload.event === 'signedOut' ||
        payload.event === 'tokenRefresh' ||
        payload.event === 'signInWithRedirect'
      ) {
        refresh();
      }
    });
    return () => unsub();
  }, [refresh]);

  const signIn = useCallback(async () => {
    await signInWithGoogle();
  }, []);

  const signOut = useCallback(async () => {
    await signOutEverywhere();
    setState({ ...initialAuthState, status: 'signed-out' });
  }, []);

  const can = useCallback(
    (permission: Permission) => state.permissions.has(permission),
    [state.permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, refresh, can }),
    [state, signIn, signOut, refresh, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
